import React, { useEffect, useMemo, useState } from "react";
import { MdTune, MdClose, MdExpandMore, MdRefresh } from "react-icons/md";
import axios from "axios";
import { classButtons } from "./ModuleFunctions";

const API = process.env.REACT_APP_API_URL;
const DEFAULTS = { rclass: "all", zone: "0", location: "0" };

// Filters the residences of one listing page by class, zone and location.
// type: the page's residence type ("Hostel", "Homestel" or "Obuasi_Campus").
// onReset: called when every filter is cleared so the page reloads its normal list.
const Filter = ({ setHostels, setActiveFilter, type = "Hostel", onReset }) => {
	const [locations, setLocations] = useState([]);
	const [zones, setZones] = useState([]);
	const [filters, setFilters] = useState(DEFAULTS);
	const [panelOpen, setPanelOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [resultCount, setResultCount] = useState(null);
	const [error, setError] = useState("");

	const isFiltering =
		filters.rclass !== DEFAULTS.rclass ||
		filters.zone !== DEFAULTS.zone ||
		filters.location !== DEFAULTS.location;

	// zones and locations only need loading once
	useEffect(() => {
		const fetchOptions = async () => {
			try {
				const [zoneRes, locRes] = await Promise.all([
					axios.get(`${API}/api/v1/zones`),
					axios.get(`${API}/api/v1/locations`),
				]);
				setZones(zoneRes.data?.data || []);
				setLocations(locRes.data?.data || []);
			} catch (err) {
				console.log(err);
			}
		};
		fetchOptions();
	}, []);

	// run the filter only when something is selected; otherwise hand back to the page
	useEffect(() => {
		if (!isFiltering) {
			setResultCount(null);
			setError("");
			setActiveFilter?.(false);
			return;
		}
		const controller = new AbortController();
		const filterSearch = async () => {
			setIsLoading(true);
			setError("");
			setActiveFilter?.(true);
			try {
				const res = await axios({
					method: "get",
					url: `${API}/api/v1/residences/filter`,
					params: {
						type,
						rclass: filters.rclass,
						zone: filters.zone,
						location: filters.location,
					},
					signal: controller.signal,
				});
				const data = res.data?.data || [];
				setHostels?.(data);
				setResultCount(data.length);
			} catch (err) {
				if (axios.isCancel(err)) return;
				console.log(err);
				setError("Couldn't apply the filter. Please try again.");
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		};
		filterSearch();
		return () => controller.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, type]);

	// only offer locations inside the chosen zone
	const zoneLocations = useMemo(() => {
		if (filters.zone === "0") return locations;
		return locations.filter(
			(loc) => (loc?.zone?._id || loc?.zone)?.toString() === filters.zone
		);
	}, [locations, filters.zone]);

	const update = (changes) => setFilters((f) => ({ ...f, ...changes }));

	const clearAll = () => {
		const wasFiltering = isFiltering;
		setFilters(DEFAULTS);
		if (wasFiltering) onReset?.();
	};

	const removeFilter = (key) => {
		const next = { ...filters, [key]: DEFAULTS[key] };
		if (key === "zone") next.location = DEFAULTS.location;
		setFilters(next);
		const stillFiltering =
			next.rclass !== DEFAULTS.rclass ||
			next.zone !== DEFAULTS.zone ||
			next.location !== DEFAULTS.location;
		if (!stillFiltering) onReset?.();
	};

	const classLabel = classButtons.find((c) => c.value === filters.rclass)?.name;
	const zoneName = zones.find((z) => z._id === filters.zone)?.name;
	const locationName = locations.find((l) => l._id === filters.location)?.name;
	const panelFilterCount =
		(filters.zone !== "0" ? 1 : 0) + (filters.location !== "0" ? 1 : 0);

	return (
		<div className="fl">
			<div className="fl-bar">
				<div className="fl-chips" role="group" aria-label="Filter by class">
					{classButtons.map((el) => {
						const selected = filters.rclass === el.value;
						return (
							<button
								key={el.value}
								type="button"
								className={`fl-chip${selected ? " is-active" : ""}`}
								aria-pressed={selected}
								onClick={() => {
									if (selected) return;
									if (el.value === "all") removeFilter("rclass");
									else update({ rclass: el.value });
								}}
							>
								{el.name}
							</button>
						);
					})}
				</div>
				<button
					type="button"
					className={`fl-toggle${panelOpen ? " is-open" : ""}`}
					aria-expanded={panelOpen}
					aria-controls="fl-panel"
					onClick={() => setPanelOpen(!panelOpen)}
				>
					<MdTune />
					<span>More filters</span>
					{panelFilterCount > 0 && <span className="fl-count">{panelFilterCount}</span>}
					<MdExpandMore className="fl-caret" />
				</button>
			</div>

			<div id="fl-panel" className={`fl-panel${panelOpen ? " is-open" : ""}`}>
				<div className="fl-panel-inner">
					<label className="fl-field">
						<span className="fl-label">Zone / Constituency</span>
						<select
							className="fl-select"
							value={filters.zone}
							onChange={(e) => update({ zone: e.target.value, location: "0" })}
						>
							<option value="0">All zones</option>
							{zones.map((z) => (
								<option key={z._id} value={z._id}>
									{z.name}
								</option>
							))}
						</select>
					</label>
					<label className="fl-field">
						<span className="fl-label">Location</span>
						<select
							className="fl-select"
							value={filters.location}
							onChange={(e) => update({ location: e.target.value })}
						>
							<option value="0">All locations</option>
							{zoneLocations.map((l) => (
								<option key={l._id} value={l._id}>
									{l.name}
								</option>
							))}
						</select>
					</label>
					<button type="button" className="fl-reset" onClick={clearAll} disabled={!isFiltering}>
						<MdRefresh /> Reset
					</button>
				</div>
			</div>

			{isFiltering && (
				<div className="fl-summary" aria-live="polite">
					<span className="fl-result">
						{isLoading ? (
							<>
								<span className="fl-spinner" aria-hidden="true" /> Filtering…
							</>
						) : error ? (
							<span className="fl-error">{error}</span>
						) : (
							<>
								<b>{resultCount}</b> residence{resultCount === 1 ? "" : "s"} found
							</>
						)}
					</span>
					{filters.rclass !== "all" && (
						<span className="fl-pill">
							{classLabel}
							<button type="button" aria-label={`Remove ${classLabel}`} onClick={() => removeFilter("rclass")}>
								<MdClose />
							</button>
						</span>
					)}
					{filters.zone !== "0" && (
						<span className="fl-pill">
							{zoneName || "Zone"}
							<button type="button" aria-label="Remove zone filter" onClick={() => removeFilter("zone")}>
								<MdClose />
							</button>
						</span>
					)}
					{filters.location !== "0" && (
						<span className="fl-pill">
							{locationName || "Location"}
							<button
								type="button"
								aria-label="Remove location filter"
								onClick={() => removeFilter("location")}
							>
								<MdClose />
							</button>
						</span>
					)}
					<button type="button" className="fl-clear" onClick={clearAll}>
						Clear all
					</button>
				</div>
			)}
		</div>
	);
};

export default Filter;
